import React from 'react';
import dynamic from 'next/dynamic';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import groupBy from 'lodash/groupBy';
import { useTranslation } from '../../src/i18n';
import { colors, fontSize } from '../../constants/styleConstants';
import { getCastGroupByRole, getOtherColumnData, getCrewGroupByRole } from '../../utils/common';
import { TP, EMPTY_INFO_MARK } from '../../constants/consts';

// dynamic imports
const CastRow = dynamic(() => import('./castRow'));
const TitledBlock = dynamic(() => import('../TitledBlock'));

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    marginBottom: 10,
  },
  types: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  teamItem: {
    display: 'flex',
    flexGrow: 1,
    flexBasis: 0,
  },
  teamItemBlock: {
    width: '100%',
    paddingTop: '2px',
  },
  title: {
    display: 'flex',
    fontWeight: 'bold',
    justifyContent: 'center',
    marginBottom: '4px',
  },
  mobile: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  toggleButton: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      background: 0,
      boxShadow: 'none',
      border: 0,
      padding: '15px 0',
    },
    '& p': {
      fontSize: fontSize.smallTitle,
      color: colors.grey3,
      margin: 0,
      textAlign: 'center',
    },
  },
  expandIcon: {
    float: 'left',
    color: colors.blueChip,
  },
  line: {
    opacity: '0.2',
    backgroundColor: '#979797',
    marginLeft: '-20px',
    width: '115%',
  },
  bold: {
    fontWeight: 'bold',
  },
  castDivision: {
    color: colors.grey2,
    marginBottom: '2px',
    display: 'flex',
    justifyContent: 'center',
  },
}));

const PerformanceCastContent = ({ other, cast, crew, showEmpty, showText }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const castGroupByRole = groupBy(cast, item => {
    return item?.workRole?.original_name || item?.workRole?.name;
  });
  const otherGroupByRole = groupBy(other, 'workRoleReference');
  const crewGroupByRole = groupBy(
    crew.map(member => ({ ...member, prof: member?.profession?.name || member?.professionReference })),
    'prof',
  );

  const crewColumnData = getCrewGroupByRole(crewGroupByRole);
  const castColumnData = getCastGroupByRole(castGroupByRole);

  const otherColumnData = getOtherColumnData(otherGroupByRole);
  const castData = cast && cast.length ? cast : [];
  const castColumn = Object.keys(castColumnData).length > 0 ? castColumnData : otherColumnData;

  return (
    <div className={classes.container}>
      {(crew?.length > 0 || showEmpty) && (
        <>
          <p className={classes.title}>{t(`${TP}.CAST_CREW`)}</p>
          <TitledBlock className={classes.teamItemBlock}>
            {isEmpty(crewColumnData) ? (
              EMPTY_INFO_MARK
            ) : (
              <CastRow
                type="crew"
                castGroupByRole={crewColumnData}
                short
                showText={showText}
                className={classes.bold}
              />
            )}
          </TitledBlock>
        </>
      )}
      {(castData?.length > 0 || showEmpty) && (
        <TitledBlock className={classes.teamItemBlock}>
          {isEmpty(castColumn) ? (
            EMPTY_INFO_MARK
          ) : (
            <>
              <span className={classes.castDivision}>~</span>
              <CastRow type="cast" castGroupByRole={castColumn} short showText={showText} />
            </>
          )}
        </TitledBlock>
      )}
      {!isEmpty(otherColumnData) && (
        <>
          <p className={classes.title}>{t(`${TP}.OTHERS`)}</p>
          <TitledBlock className={classes.teamItemBlock}>
            {isEmpty(otherColumnData) ? (
              EMPTY_INFO_MARK
            ) : (
              <CastRow type="other" castGroupByRole={otherColumnData} short showText={showText} />
            )}
          </TitledBlock>
        </>
      )}
    </div>
  );
};

PerformanceCastContent.propTypes = {
  cast: PropTypes.array,
  crew: PropTypes.array,
};

export default PerformanceCastContent;
